import React from 'react';
import { TextField, TextFieldProps, useTheme } from '@mui/material';

interface EliceTextareaProps extends Omit<TextFieldProps, 'type'> {
  maxLength?: number;
  hideBorder?: boolean;
  type: 'default' | 'textarea';
  isCompleted?: boolean;
  borderColor?: string;
  hoverBorderColor?: string;
  focusedBorderColor?: string;
  disabledBackgroundColor?: string;
}

const EliceTextarea = ({
  type = 'textarea',
  variant = 'outlined',
  rows = 5,
  placeholder = 'Placeholder...',
  multiline = true,
  maxLength,
  value,
  onChange,
  hideBorder,
  disabled,
  isCompleted,
  borderColor,
  hoverBorderColor,
  focusedBorderColor,
  disabledBackgroundColor,
  sx,
  ...rest
}: EliceTextareaProps) => {
  const theme = useTheme();
  const shouldHideBorder = hideBorder || disabled;

  if (type === 'textarea') {
    const defaultBorderColor = borderColor || '#d0d0d0';
    const defaultHoverBorderColor = hoverBorderColor || `${theme.palette.primary.main}80`;
    const defaultFocusedBorderColor = focusedBorderColor || theme.palette.primary.main;
    const defaultDisabledBackgroundColor = disabledBackgroundColor || '#f5f5f5';

    const baseSx = {
      width: '100%',
      '& .MuiOutlinedInput-root': {
        backgroundColor: disabled ? defaultDisabledBackgroundColor : theme.palette.background.paper,
        width: '100%',
        borderRadius: '0.5rem',
        '& fieldset': {
          borderColor: shouldHideBorder ? 'transparent' : defaultBorderColor,
        },
        '&:hover fieldset': {
          borderColor: shouldHideBorder ? 'transparent' : defaultHoverBorderColor,
        },
        '&.Mui-focused fieldset': {
          borderColor: shouldHideBorder ? 'transparent' : defaultFocusedBorderColor,
        },
        '&.Mui-disabled': {
          '& fieldset': {
            borderColor: 'transparent',
          },
        },
      },
      '& .MuiInputBase-input': {
        fontSize: theme.typography.subtitle1?.fontSize || '1.3rem',
        fontWeight: theme.typography.subtitle1?.fontWeight || 500,
        lineHeight: theme.typography.subtitle1?.lineHeight || 1.5,
        alignItems: 'flex-start',
        color: theme.palette.text.primary,
        WebkitTextFillColor: theme.palette.text.primary,
      },
      '& .MuiInputBase-input.Mui-disabled': {
        color: theme.palette.text.secondary,
        WebkitTextFillColor: theme.palette.text.secondary,
      },
      '& .MuiInputBase-input::placeholder': {
        color: theme.palette.text.secondary,
        fontSize: theme.typography.subtitle1?.fontSize || '1.3rem',
      },
    };

    const mergedSx = sx !== undefined
      ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])]
      : baseSx;

    return (
      <TextField
        value={value}
        variant={variant}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled || isCompleted}
        multiline={multiline}
        rows={rows}
        inputProps={{ maxLength }}
        sx={mergedSx}
        {...rest}
      />
    );
  }

  // default variant - 기존 스타일 유지
  const defaultDisabledBackgroundColor = disabledBackgroundColor || '#f5f5f5';
  const baseSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: disabled ? defaultDisabledBackgroundColor : theme.palette.background.paper,
      borderRadius: '1.5rem',
      padding: '1.375rem 1.5rem',
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: `${theme.palette.primary.main}80`, // primary에 opacity 50%
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': {
      fontSize: theme.typography.subtitle1?.fontSize || '1.375rem',
      fontWeight: theme.typography.subtitle1?.fontWeight || 700,
      lineHeight: theme.typography.subtitle1?.lineHeight || 1.925,
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.secondary,
      },
    },
  };

  const mergedSx = sx !== undefined
    ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])]
    : baseSx;

  return (
    <TextField
      value={value}
      onChange={onChange}
      multiline
      rows={rows}
      placeholder={placeholder}
      disabled={disabled}
      sx={mergedSx}
      {...rest}
    />
  );
};

export default EliceTextarea;
